import { createContext, useContext, useEffect, useId, useRef, useState, type DragEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlignCenter, AlignJustify, AlignLeft, ChevronDown, ChevronUp, Clock, ImagePlus, MapPin, Menu, Phone, Plus, Share2, Trash2, Type, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { LESSONS_PER_PAGE, derivedNav } from "@baze/engine";
import { DONATION_FIELD_TYPES, SECTION_META, donationFields, isSafeHref, youtubeId, type DonationField, type SectionNode, type WebsiteDefinition } from "@baze/schema";
import { Button } from "@/components/ui/button";
import { LessonContentField } from "./lesson-content";
import { Field } from "@/components/ui/field";
import { Segmented } from "@/components/ui/segmented";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/select";
import { SwitchRow } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { formatRupiahDigits, parseRupiahInput } from "@/lib/format";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";
import { updateContent, updateFooterChrome, updateNavbarChrome } from "./model/ops";
import { sectionHeading } from "./outline";
import { ProductFeatureForm, ProductsSource } from "./store-forms";

type Apply = (next: WebsiteDefinition, key?: string) => void;

/**
 * Which list item the forms are about, as a content path ("groups", "1", "topics", "3"):
 * - `settings`: the section's own fields, every list hidden (large workspace);
 * - `item`: only that item's fields (large workspace);
 * - `open`: the normal form with that item opened and scrolled to (a click on it in the preview).
 */
export type ListFocus = { mode: "settings" } | { mode: "item"; path: string[] } | { mode: "open"; path: string[]; nonce: number };

export const ListFocusContext = createContext<{ focus: ListFocus | null; roomy: boolean }>({ focus: null, roomy: false });
/** Path of the list item a nested form sits in. */
const ItemPathContext = createContext<string[]>([]);

const startsWith = (path: string[], prefix: string[]) => prefix.every((seg, i) => path[i] === seg);
type Img = { src: string; alt: string; assetId?: string; width?: number; height?: number };
type Cta = { label: string; kind: "whatsapp" | "link"; href?: string; message?: string };

export function SectionForm({ siteId, def, section, apply }: { siteId: string; def: WebsiteDefinition; section: SectionNode; apply: Apply }) {
  const set = (path: string, value: unknown) => apply(updateContent(def, section.id, path, value), `${section.id}:${path}`);

  if (section.type === "navbar") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <SwitchRow
          id="nav-title"
          label={t.editor.fields.showSiteTitle}
          hint={def.site.logo ? t.editor.fields.showSiteTitleHint : t.editor.fields.showSiteTitleNoLogo}
          checked={c.showTitle !== false}
          onCheckedChange={(v) => apply(updateNavbarChrome(def, "showTitle", v), "navbar:showTitle")}
        />
        <SwitchRow id="nav-cta" label={t.editor.fields.showCta} checked={c.showCta !== false} onCheckedChange={(v) => set("showCta", v)} />
        {c.showCta !== false ? (
          <>
            <TextField label={t.editor.fields.ctaLabel} value={c.ctaLabel ?? ""} optional onChange={(v) => set("ctaLabel", v || undefined)} />
            <NavCtaTarget def={def} section={section} apply={apply} />
          </>
        ) : null}
        <SwitchRow id="nav-bar" label={t.editor.fields.mobileBar} checked={!!c.mobileBar} onCheckedChange={(v) => set("mobileBar", v || undefined)} />
      </div>
    );
  }
  if (section.type === "hero") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ImageField siteId={siteId} label={section.variant === "slideshow" ? t.editor.inspector.itemN(t.editor.fields.slide, 1) : t.editor.fields.image} value={c.image} onChange={(v) => set("image", v)} />
        {section.variant === "slideshow" ? (
          <div className="space-y-2">
            {(c.images ?? []).map((image, index, all) => (
              <ImageField
                key={image.src + index}
                siteId={siteId}
                label={t.editor.inspector.itemN(t.editor.fields.slide, index + 2)}
                value={image}
                onChange={(v) => {
                  const next = v ? all.map((img, i) => (i === index ? v : img)) : all.filter((_, i) => i !== index);
                  set("images", next.length ? next : undefined);
                }}
              />
            ))}
            {(c.images?.length ?? 0) < 8 ? (
              <ImageField siteId={siteId} label={t.editor.fields.addSlide} hint={t.editor.fields.slidesHint} onChange={(v) => { if (v) set("images", [...(c.images ?? []), v]); }} />
            ) : null}
          </div>
        ) : null}
        <CtaField label={t.editor.fields.primaryCta} value={c.primaryCta} onChange={(v) => set("primaryCta", v)} />
        <CtaField label={t.editor.fields.secondaryCta} value={c.secondaryCta} onChange={(v) => set("secondaryCta", v)} />
      </div>
    );
  }
  if (section.type === "about") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <TextField label={t.editor.fields.body} value={c.body} multiline max={1500} onChange={(v) => set("body", v)} />
        <ImageField siteId={siteId} label={t.editor.fields.image} value={c.image} onChange={(v) => set("image", v)} />
        <StringList label={t.editor.fields.highlights} itemLabel={t.editor.fields.highlight} items={c.highlights ?? []} onChange={(items) => set("highlights", items.length ? items : undefined)} />
      </div>
    );
  }
  if (section.type === "services") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.items}
          path="items"
          items={c.items}
          blank={() => ({ title: "Layanan baru" })}
          titleOf={(item) => item.title}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.itemTitle} value={item.title} onChange={(v) => update({ title: v || item.title })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <PriceField value={item.price} onChange={(v) => update({ price: v })} />
              <ImageField siteId={siteId} label={t.editor.fields.image} value={item.image} onChange={(v) => update({ image: v })} />
              <CtaField label={t.editor.fields.cardButton} value={item.cta} initial={{ label: "Tanya", kind: "whatsapp" }} hint={t.editor.fields.cardButtonHint} onChange={(v) => update({ cta: v })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "products") {
    const c = section.content;
    const fromStore = c.dataSource?.type === "store";
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ProductsSource siteId={siteId} section={section} set={set} />
        {fromStore ? null : <ItemStack
          label={t.editor.fields.items}
          path="items"
          items={c.items}
          blank={() => ({ name: "Produk baru" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.itemName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <PriceField value={item.price} onChange={(v) => update({ price: v })} />
              <TextField
                label={t.editor.fields.priceNote}
                value={item.priceNote ?? ""}
                optional
                placeholder={t.editor.fields.priceNotePlaceholder}
                onChange={(v) => update({ priceNote: v || undefined })}
              />
              <TextField label={t.editor.fields.badge} value={item.badge ?? ""} optional onChange={(v) => update({ badge: v || undefined })} />
              <ImageField siteId={siteId} label={t.editor.fields.image} value={item.image} onChange={(v) => update({ image: v })} />
              <CtaField label={t.editor.fields.cardButton} value={item.cta} initial={{ label: "Pesan", kind: "whatsapp" }} hint={t.editor.fields.cardButtonHint} onChange={(v) => update({ cta: v })} />
              <details className="rounded-lg bg-white ring-1 ring-slate-900/8" open={!!(item.artist || item.medium || item.size || item.year || item.status)}>
                <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-700">{t.editor.fields.artworkDetails}</summary>
                <div className="space-y-3 px-3 pb-3">
                  <TextField label={t.editor.fields.artist} value={item.artist ?? ""} optional onChange={(v) => update({ artist: v || undefined })} />
                  <TextField
                    label={t.editor.fields.medium}
                    value={item.medium ?? ""}
                    optional
                    placeholder={t.editor.fields.mediumPlaceholder}
                    onChange={(v) => update({ medium: v || undefined })}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <TextField label={t.editor.fields.size} value={item.size ?? ""} optional placeholder="90 × 120 cm" onChange={(v) => update({ size: v || undefined })} />
                    <TextField label={t.editor.fields.year} value={item.year ?? ""} optional placeholder="2025" onChange={(v) => update({ year: v || undefined })} />
                  </div>
                  <Field label={t.editor.fields.status} optional>
                    <NativeSelect
                      selectSize="sm"
                      value={item.status ?? ""}
                      onChange={(e) => update({ status: (e.target.value || undefined) as typeof item.status })}
                    >
                      <option value="">{t.editor.fields.statusNone}</option>
                      <option value="available">{t.editor.fields.statusAvailable}</option>
                      <option value="reserved">{t.editor.fields.statusReserved}</option>
                      <option value="sold">{t.editor.fields.statusSold}</option>
                    </NativeSelect>
                  </Field>
                </div>
              </details>
            </>
          )}
        />}
      </div>
    );
  }
  if (section.type === "product-feature") {
    return <ProductFeatureForm siteId={siteId} section={section} set={set} />;
  }
  if (section.type === "menu") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.categories}
          path="categories"
          items={c.categories}
          blank={() => ({ name: "Kategori", items: [{ name: "Menu baru" }] })}
          titleOf={(item) => item.name}
          onChange={(items) => set("categories", items)}
          render={(cat, update) => (
            <>
              <TextField label={t.editor.fields.categoryName} value={cat.name} onChange={(v) => update({ name: v || cat.name })} />
              <ItemStack
                label={t.editor.fields.menuItems}
                path="items"
                items={cat.items}
                blank={() => ({ name: "Menu baru" })}
                titleOf={(item) => item.name}
                onChange={(items) => update({ items })}
                render={(item, updateItem) => (
                  <>
                    <TextField label={t.editor.fields.itemName} value={item.name} onChange={(v) => updateItem({ name: v || item.name })} />
                    <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional onChange={(v) => updateItem({ description: v || undefined })} />
                    <PriceField value={item.price} onChange={(v) => updateItem({ price: v })} />
                    <CtaField label={t.editor.fields.cardButton} value={item.cta} initial={{ label: "Pesan", kind: "whatsapp" }} hint={t.editor.fields.cardButtonHint} onChange={(v) => updateItem({ cta: v })} />
                  </>
                )}
              />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "gallery") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title ?? ""} titleOptional onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <div className="space-y-2">
          {c.images.map((image, index) => (
            <ImageField key={image.src + index} siteId={siteId} label={t.editor.inspector.itemN(t.editor.fields.photo, index + 1)} value={image} onChange={(v) => set("images", v ? c.images.map((img, i) => (i === index ? v : img)) : c.images.filter((_, i) => i !== index))} />
          ))}
          <ImageField siteId={siteId} label={t.editor.fields.images} onChange={(v) => { if (v) set("images", [...c.images, v]); }} />
        </div>
      </div>
    );
  }
  if (section.type === "logos") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title ?? ""} titleOptional onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <SwitchRow id={`logos-grey-${section.id}`} label={t.editor.fields.logosGrayscale} checked={!!c.grayscale} onCheckedChange={(v) => set("grayscale", v || undefined)} />
        <p className="text-[12.5px] leading-snug text-slate-500">{t.editor.fields.logosHint}</p>
        <ItemStack
          label={t.editor.fields.logos}
          path="items"
          items={c.items}
          blank={() => ({ name: t.editor.fields.logoNameBlank })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <ImageField siteId={siteId} label={t.editor.fields.logoImage} value={item.logo} onChange={(v) => update({ logo: v })} />
              <TextField label={t.editor.fields.logoName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              <LinkField label={t.editor.fields.logoUrl} value={item.url} placeholder="https://…" onChange={(v) => update({ url: v })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "videos") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title ?? ""} titleOptional description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <p className="text-[12.5px] leading-snug text-slate-500">{t.editor.fields.videosHint}</p>
        <ItemStack
          label={t.editor.fields.videos}
          path="items"
          items={c.items}
          blank={() => ({ title: t.editor.fields.videoBlank })}
          titleOf={(item) => item.title || t.editor.fields.videoBlank}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <LinkField label={t.editor.fields.lessonVideo} value={item.url} placeholder="https://youtu.be/…" valid={(v) => !!youtubeId(v)} invalidText={t.editor.fields.lessonVideoInvalid} onChange={(v) => update({ url: v })} />
              <TextField label={t.editor.fields.videoTitle} value={item.title ?? ""} optional onChange={(v) => update({ title: v || undefined })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "testimonials") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <ItemStack
          label={t.editor.fields.items}
          path="items"
          items={c.items}
          blank={() => ({ quote: "Ulasan pelanggan.", name: "Nama pelanggan" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              {item.sample ? <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">{t.editor.fields.sampleWarning}</p> : null}
              <TextField label={t.editor.fields.quote} value={item.quote} multiline onChange={(v) => update({ quote: v || item.quote, sample: undefined })} />
              <TextField label={t.editor.fields.name} value={item.name} onChange={(v) => update({ name: v || item.name, sample: undefined })} />
              <TextField label={t.editor.fields.role} value={item.role ?? ""} optional onChange={(v) => update({ role: v || undefined })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "faq") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <ItemStack
          label={t.editor.fields.items}
          path="items"
          items={c.items}
          blank={() => ({ question: "Pertanyaan baru", answer: "Jawaban singkat." })}
          titleOf={(item) => item.question}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.question} value={item.question} onChange={(v) => update({ question: v || item.question })} />
              <TextField label={t.editor.fields.answer} value={item.answer} multiline onChange={(v) => update({ answer: v || item.answer })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "stats") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title ?? ""} titleOptional onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <ItemStack
          label={t.editor.fields.items}
          path="items"
          items={c.items}
          blank={() => ({ value: "0", label: "Keterangan" })}
          titleOf={(item) => item.label}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.value} value={item.value} onChange={(v) => update({ value: v || item.value })} />
              <TextField label={t.editor.fields.label} value={item.label} onChange={(v) => update({ label: v || item.label })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "opening-hours") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <TextField label={t.editor.fields.note} value={c.note ?? ""} optional multiline onChange={(v) => set("note", v || undefined)} />
        <p className="text-[13px] text-slate-500">{t.editor.inspector.fromBusinessInfo}</p>
      </div>
    );
  }
  if (section.type === "location") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <SwitchRow id="show-map" label={t.editor.fields.showMap} checked={c.showMap !== false} onCheckedChange={(v) => set("showMap", v)} />
        <p className="text-[13px] text-slate-500">{t.editor.inspector.fromBusinessInfo}</p>
      </div>
    );
  }
  if (section.type === "contact") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <TextField label={t.editor.fields.message} value={c.message ?? ""} optional multiline onChange={(v) => set("message", v || undefined)} />
        <SwitchRow id="show-wa" label={t.editor.fields.showWhatsapp} checked={c.showWhatsapp !== false} onCheckedChange={(v) => set("showWhatsapp", v)} />
        <SwitchRow id="show-phone" label={t.editor.fields.showPhone} checked={c.showPhone !== false} onCheckedChange={(v) => set("showPhone", v)} />
        <SwitchRow id="show-email" label={t.editor.fields.showEmail} checked={c.showEmail !== false} onCheckedChange={(v) => set("showEmail", v)} />
        <SwitchRow id="show-social" label={t.editor.fields.showSocial} checked={c.showSocial !== false} onCheckedChange={(v) => set("showSocial", v)} />
      </div>
    );
  }
  if (section.type === "pricing") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.plans}
          path="plans"
          items={c.plans}
          blank={() => ({ name: "Paket baru", features: ["Fitur utama"] })}
          titleOf={(item) => item.name}
          onChange={(items) => set("plans", items)}
          render={(plan, update) => (
            <>
              <TextField label={t.editor.fields.planName} value={plan.name} onChange={(v) => update({ name: v || plan.name })} />
              <PriceField value={plan.price} onChange={(v) => update({ price: v })} />
              <TextField label={t.editor.fields.period} value={plan.period ?? ""} optional onChange={(v) => update({ period: v || undefined })} />
              <StringList label={t.editor.fields.features} itemLabel={t.editor.fields.feature} items={plan.features} onChange={(features) => update({ features: features.length ? features : ["Fitur utama"] })} />
              <SwitchRow id={`plan-hi-${plan.name}`} label={t.editor.fields.highlighted} checked={!!plan.highlighted} onCheckedChange={(v) => update({ highlighted: v || undefined })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "cta") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <CtaField label={t.editor.fields.button} value={c.button} required onChange={(v) => { if (v) set("button", v); }} />
      </div>
    );
  }
  if (section.type === "timeline") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.steps}
          path="items"
          items={c.items}
          blank={() => ({ title: "Langkah baru" })}
          titleOf={(item) => item.title}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.stepLabel} value={item.label ?? ""} optional onChange={(v) => update({ label: v || undefined })} />
              <TextField label={t.editor.fields.itemTitle} value={item.title} onChange={(v) => update({ title: v || item.title })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <ImageField siteId={siteId} label={t.editor.fields.image} value={item.image} onChange={(v) => update({ image: v })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "events") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <SwitchRow id="ev-cal" label={t.editor.fields.showCalendar} checked={c.showCalendar !== false} onCheckedChange={(v) => set("showCalendar", v)} />
        <ItemStack
          label={t.editor.fields.events}
          path="items"
          items={c.items}
          blank={() => ({ name: "Acara baru" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.eventName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              <Field label={t.editor.fields.date} optional>
                <Input type="date" value={item.date ?? ""} onChange={(e) => update({ date: e.target.value || undefined })} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label={t.editor.fields.startTime} optional>
                  <Input type="time" value={item.startTime ?? ""} onChange={(e) => update({ startTime: e.target.value || undefined })} />
                </Field>
                <Field label={t.editor.fields.endTime} optional>
                  <Input type="time" value={item.endTime ?? ""} onChange={(e) => update({ endTime: e.target.value || undefined })} />
                </Field>
              </div>
              <TextField label={t.editor.fields.timeNote} value={item.timeNote ?? ""} optional onChange={(v) => update({ timeNote: v || undefined })} />
              <TextField label={t.editor.fields.venue} value={item.venue ?? ""} optional onChange={(v) => update({ venue: v || undefined })} />
              <TextField label={t.editor.fields.address} value={item.address ?? ""} optional multiline onChange={(v) => update({ address: v || undefined })} />
              <TextField label={t.editor.fields.mapUrl} value={item.mapUrl ?? ""} optional onChange={(v) => update({ mapUrl: v.trim() || undefined })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "team") {
    const c = section.content;
    // The "couple" variant is a wedding's bride and groom: name, "Putri dari", and the parents.
    const couple = section.variant === "couple";
    const f = t.editor.fields;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={couple ? f.couple : f.people}
          path="items"
          items={c.items}
          blank={() => ({ name: couple ? f.coupleName : "Nama" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={couple ? f.coupleName : f.name} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              <TextField label={couple ? f.coupleRole : f.personRole} value={item.role ?? ""} optional onChange={(v) => update({ role: v || undefined })} />
              <TextField label={couple ? f.coupleParents : f.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <TextField label={t.editor.fields.instagram} value={item.instagram ?? ""} optional onChange={(v) => update({ instagram: v.replace(/^@/, "").replace(/[^A-Za-z0-9._]/g, "").slice(0, 30) || undefined })} />
              <ImageField siteId={siteId} label={t.editor.fields.photo} value={item.image} onChange={(v) => update({ image: v })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "quote") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <TextField label={t.editor.fields.eyebrow} value={c.eyebrow ?? ""} optional onChange={(v) => set("eyebrow", v || undefined)} />
        <TextField label={t.editor.fields.quoteText} value={c.text} multiline max={600} error={!c.text.trim() ? t.editor.requiredField : undefined} onChange={(v) => set("text", v)} />
        <TextField label={t.editor.fields.cite} value={c.cite ?? ""} optional onChange={(v) => set("cite", v || undefined)} />
      </div>
    );
  }
  if (section.type === "gift") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.accounts}
          path="accounts"
          items={c.accounts}
          blank={() => ({ provider: "Nama bank", name: "Nama pemilik", number: "0000000000" })}
          titleOf={(item) => item.provider}
          onChange={(items) => set("accounts", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.provider} value={item.provider} onChange={(v) => update({ provider: v || item.provider })} />
              <TextField label={t.editor.fields.accountNumber} value={item.number} onChange={(v) => update({ number: v.replace(/[^0-9A-Za-z .+-]/g, "").slice(0, 40) || item.number })} />
              <TextField label={t.editor.fields.accountName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
            </>
          )}
        />
        <TextField label={t.editor.fields.giftAddress} value={c.address ?? ""} optional multiline onChange={(v) => set("address", v || undefined)} />
      </div>
    );
  }
  if (section.type === "links") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title ?? ""} titleOptional onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <ItemStack
          label={t.editor.fields.links}
          path="items"
          items={c.items}
          blank={() => ({ label: "Tautan baru", href: "https://example.com" })}
          titleOf={(item) => item.label}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.linkLabel} value={item.label} onChange={(v) => update({ label: v || item.label })} />
              <UrlField value={item.href} onChange={(v) => update({ href: v })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional onChange={(v) => update({ description: v || undefined })} />
              <SwitchRow id={`link-hi-${item.label}`} label={t.editor.fields.linkHighlight} checked={!!item.highlight} onCheckedChange={(v) => update({ highlight: v || undefined })} />
              <SwitchRow id={`link-sp-${item.label}`} label={t.editor.fields.linkSponsored} checked={!!item.sponsored} onCheckedChange={(v) => update({ sponsored: v || undefined })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "picks") {
    const c = section.content;
    const affiliate = c.affiliate !== false;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <SwitchRow id="picks-aff" label={t.editor.fields.affiliate} checked={affiliate} onCheckedChange={(v) => set("affiliate", v ? undefined : false)} />
        {affiliate ? (
          <TextField label={t.editor.fields.disclosure} value={c.disclosure ?? ""} optional multiline onChange={(v) => set("disclosure", v || undefined)} />
        ) : null}
        <TextField label={t.editor.fields.pickButton} value={c.linkLabel ?? ""} optional onChange={(v) => set("linkLabel", v || undefined)} />
        <ItemStack
          label={t.editor.fields.items}
          path="items"
          items={c.items}
          blank={() => ({ name: "Produk baru", href: "https://example.com" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.itemName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              <UrlField value={item.href} onChange={(v) => update({ href: v })} />
              <TextField label={t.editor.fields.store} value={item.store ?? ""} optional onChange={(v) => update({ store: v || undefined })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <PriceField value={item.price} onChange={(v) => update({ price: v })} />
              <TextField label={t.editor.fields.badge} value={item.badge ?? ""} optional onChange={(v) => update({ badge: v || undefined })} />
              <ImageField siteId={siteId} label={t.editor.fields.image} value={item.image} onChange={(v) => update({ image: v })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "lessons") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <p className="text-[13px] leading-relaxed text-slate-500">{t.editor.fields.lessonsNote}</p>
        <ItemStack
          label={t.editor.fields.lessonGroups}
          path="groups"
          items={c.groups}
          blank={() => ({ label: `Pertemuan ${c.groups.length + 1}`, title: "Pertemuan baru", topics: [{ title: "Topik baru" }] })}
          titleOf={(g) => [g.label, g.title].filter(Boolean).join(" · ")}
          onChange={(groups) => set("groups", groups)}
          render={(g, update) => (
            <>
              <TextField label={t.editor.fields.lessonGroupLabel} value={g.label ?? ""} optional onChange={(v) => update({ label: v || undefined })} />
              <TextField label={t.editor.fields.title} value={g.title} onChange={(v) => update({ title: v || g.title })} />
              <TextField label={t.editor.fields.description} value={g.description ?? ""} optional onChange={(v) => update({ description: v || undefined })} />
              <ItemStack
                label={t.editor.fields.lessonTopics}
                path="topics"
                items={g.topics}
                blank={() => ({ title: "Topik baru" })}
                titleOf={(topic) => topic.title}
                onChange={(topics) => update({ topics })}
                render={(topic, updateTopic) => (
                  <>
                    <TextField label={t.editor.fields.title} value={topic.title} onChange={(v) => updateTopic({ title: v || topic.title })} />
                    <TextField label={t.editor.fields.lessonSummary} value={topic.summary ?? ""} optional multiline onChange={(v) => updateTopic({ summary: v || undefined })} />
                    <StringList label={t.editor.fields.lessonPoints} itemLabel={t.editor.fields.lessonPoint} items={topic.points ?? []} onChange={(points) => updateTopic({ points: points.length ? points : undefined })} />
                    <LessonContentField siteId={siteId} topic={topic} onChange={(patch) => updateTopic(patch)} />
                    <LinkField label={t.editor.fields.lessonPdf} value={topic.pdf} placeholder="https://drive.google.com/…" onChange={(v) => updateTopic({ pdf: v })} />
                    <LinkField label={t.editor.fields.lessonVideo} value={topic.video} placeholder="https://youtu.be/…" valid={(v) => !!youtubeId(v)} invalidText={t.editor.fields.lessonVideoInvalid} onChange={(v) => updateTopic({ video: v })} />
                    <ImageField siteId={siteId} label={t.editor.fields.image} value={topic.image} onChange={(v) => updateTopic({ image: v })} />
                  </>
                )}
              />
            </>
          )}
        />
        <SwitchRow id="lessons-search" label={t.editor.fields.lessonShowSearch} checked={c.showSearch !== false} onCheckedChange={(v) => set("showSearch", v ? undefined : false)} />
        <SwitchRow id="lessons-filter" label={t.editor.fields.lessonShowFilter} checked={c.showFilter !== false} onCheckedChange={(v) => set("showFilter", v ? undefined : false)} />
        <LessonsPerPage perPage={c.perPage} total={c.groups.reduce((n, g) => n + g.topics.length, 0)} onChange={(v) => set("perPage", v)} />
        <TextField label={t.editor.fields.lessonNoteBody} value={c.note?.body ?? ""} optional multiline onChange={(v) => set("note", v ? { ...c.note, body: v } : undefined)} />
        {c.note ? <TextField label={t.editor.fields.lessonNoteTitle} value={c.note.title ?? ""} optional onChange={(v) => set("note", { ...c.note, title: v || undefined })} /> : null}
        <details className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-900/8">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800">{t.editor.fields.buttonTexts}</summary>
          <div className="space-y-3 pt-3">
            <TextField label={t.editor.fields.lessonSearchPlaceholder} value={c.searchPlaceholder ?? ""} placeholder="Cari materi…" optional onChange={(v) => set("searchPlaceholder", v || undefined)} />
            <TextField label={t.editor.fields.lessonPdfLabel} value={c.pdfLabel ?? ""} placeholder="Buka PDF" optional onChange={(v) => set("pdfLabel", v || undefined)} />
            <TextField label={t.editor.fields.lessonVideoLabel} value={c.videoLabel ?? ""} placeholder="Tonton di YouTube" optional onChange={(v) => set("videoLabel", v || undefined)} />
            <TextField label={t.editor.fields.lessonBackLabel} value={c.backLabel ?? ""} placeholder="Kembali ke daftar materi" optional onChange={(v) => set("backLabel", v || undefined)} />
          </div>
        </details>
      </div>
    );
  }
  if (section.type === "blog-latest") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <Field label={t.editor.fields.postCount}>
          <NativeSelect selectSize="sm" value={String(c.count ?? 3)} onChange={(e) => set("count", Number(e.target.value))}>
            {[3, 6].map((n) => (
              <option key={n} value={n}>
                {t.editor.fields.postCountOption(n)}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <TextField label={t.editor.fields.postLinkLabel} value={c.linkLabel ?? ""} optional onChange={(v) => set("linkLabel", v || undefined)} />
        <p className="text-[13px] leading-relaxed text-slate-500">{t.editor.fields.blogLatestNote}</p>
      </div>
    );
  }
  if (section.type === "donation") {
    const c = section.content;
    const askAmount = c.askAmount !== false;
    const askBank = c.askBank !== false;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.donationFields}
          path="fields"
          items={donationFields(c)}
          blank={() => ({ label: "Pertanyaan baru", type: "text" as const })}
          titleOf={(q) => q.label}
          // The first edit writes the whole list, defaults included (askAddress only shapes the defaults).
          onChange={(fields) => set("fields", fields)}
          render={(q, update) => <DonationFieldEditor field={q} update={update} />}
        />
        <SwitchRow id="form-amount" label={t.editor.fields.formAskAmount} checked={askAmount} onCheckedChange={(v) => set("askAmount", v ? undefined : false)} />
        {askAmount ? <AmountListField label={t.editor.fields.donationPresets} value={c.presets ?? []} onChange={(v) => set("presets", v.length ? v : undefined)} /> : null}
        <SwitchRow id="form-bank" label={t.editor.fields.formAskBank} checked={askBank} onCheckedChange={(v) => set("askBank", v ? undefined : false)} />
        {askBank ? (
          <ItemStack
            label={t.editor.fields.donationAccounts}
            path="accounts"
            items={c.accounts}
            blank={() => ({ provider: "Nama bank", name: "Nama pemilik", number: "0000000000" })}
            titleOf={(item) => item.provider}
            onChange={(items) => set("accounts", items)}
            render={(item, update) => (
              <>
                <TextField label={t.editor.fields.provider} value={item.provider} onChange={(v) => update({ provider: v || item.provider })} />
                <TextField label={t.editor.fields.accountNumber} value={item.number} onChange={(v) => update({ number: v.replace(/[^0-9A-Za-z .+-]/g, "").slice(0, 40) || item.number })} />
                <TextField label={t.editor.fields.accountName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              </>
            )}
          />
        ) : null}
        <TextField label={t.editor.fields.formSubmitLabel} value={c.submitLabel ?? ""} optional placeholder="Kirim via WhatsApp" onChange={(v) => set("submitLabel", v.slice(0, 40) || undefined)} />
        <TextField label={t.editor.fields.formProgramLabel} value={c.programLabel ?? ""} optional placeholder="Program" onChange={(v) => set("programLabel", v.slice(0, 30) || undefined)} />
        <TextField
          label={t.editor.fields.donationMessage}
          value={c.message ?? ""}
          optional
          multiline
          placeholder={askAmount ? "Halo {{nama_usaha}}, saya ingin berdonasi." : "Halo {{nama_usaha}}, berikut data saya."}
          onChange={(v) => set("message", v || undefined)}
        />
        <p className="text-[13px] leading-relaxed text-slate-500">{t.editor.fields.donationNote}</p>
      </div>
    );
  }
  if (section.type === "wishes") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <SwitchRow id="wish-att" label={t.editor.fields.askAttendance} checked={!!c.askAttendance} onCheckedChange={(v) => set("askAttendance", v || undefined)} />
        <p className="text-[13px] leading-relaxed text-slate-500">{t.editor.fields.wishesNote}</p>
      </div>
    );
  }
  if (section.type === "rsvp") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <Field label={t.editor.fields.rsvpDeadline} optional>
          <Input type="date" value={c.deadline ?? ""} onChange={(e) => set("deadline", e.target.value || undefined)} />
        </Field>
        <Field label={t.editor.fields.maxPax}>
          <NativeSelect selectSize="sm" value={String(c.maxPax ?? 2)} onChange={(e) => set("maxPax", Number(e.target.value))}>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {t.editor.fields.paxOption(n)}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <SwitchRow id="rsvp-note" label={t.editor.fields.askNote} checked={!!c.askNote} onCheckedChange={(v) => set("askNote", v || undefined)} />
        <p className="text-[13px] leading-relaxed text-slate-500">{t.editor.fields.rsvpNote}</p>
      </div>
    );
  }
  if (section.type === "countdown") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <Field label={t.editor.fields.date}>
          <Input type="date" value={c.date} onChange={(e) => e.target.value && set("date", e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t.editor.fields.startTime} optional>
            <Input type="time" value={c.time ?? ""} onChange={(e) => set("time", e.target.value || undefined)} />
          </Field>
          <Field label={t.editor.fields.zone} optional>
            <NativeSelect selectSize="sm" value={c.timeNote ?? "WIB"} onChange={(e) => set("timeNote", e.target.value)}>
              <option value="WIB">WIB</option>
              <option value="WITA">WITA</option>
              <option value="WIT">WIT</option>
            </NativeSelect>
          </Field>
        </div>
        <TextField label={t.editor.fields.doneText} value={c.doneText ?? ""} optional onChange={(v) => set("doneText", v || undefined)} />
        <SwitchRow id="cd-cal" label={t.editor.fields.showCalendar} checked={c.showCalendar !== false} onCheckedChange={(v) => set("showCalendar", v)} />
      </div>
    );
  }
  if (section.type !== "footer") return null;
  return <FooterFields def={def} section={section} apply={apply} />;
}

const FOOTER_ALIGNS = ["start", "center", "spread"] as const;

/** Which footer blocks show, and how the columns line up. Visibility and alignment apply to every page. */
function FooterFields({ def, section, apply }: { def: WebsiteDefinition; section: Extract<SectionNode, { type: "footer" }>; apply: Apply }) {
  const c = section.content;
  const setText = (value: string | undefined) => apply(updateContent(def, section.id, "text", value), `${section.id}:text`);
  const setChrome = (key: "showSocial" | "showHours" | "showAddress" | "showNav" | "showContact" | "showTitle" | "align", value: boolean | "start" | "center" | "spread") =>
    apply(updateFooterChrome(def, key, value), `footer:${key}`);
  const align = c.align ?? (section.variant === "columns" ? "spread" : "center");
  const { whatsapp, phone, email, address, city } = def.site.contact;
  const hasSocial = Object.values(def.site.social).some(Boolean);
  const hasContact = !!(whatsapp || phone || email);
  const hasAddress = !!(address?.trim() || city?.trim());
  const hasHours = (def.site.hours?.length ?? 0) > 0;
  const home = def.pages.find((p) => p.slug === "/") ?? def.pages[0];
  const hasNav = derivedNav(def, home).length > 0;
  const empty = t.editor.fields.footerEmptyInfo;
  return (
    <div className="space-y-3">
      <TextField label={t.editor.fields.footerText} value={c.text ?? ""} optional multiline onChange={(v) => setText(v || undefined)} />
      <div className="space-y-3 rounded-xl px-3 py-3 ring-1 ring-line">
        <div>
          <p className="text-sm font-semibold text-slate-800">{t.editor.fields.footerParts}</p>
          <p className="mt-0.5 text-[13px] leading-snug text-slate-500">{t.editor.fields.footerPartsHint}</p>
        </div>
        <div className="space-y-1.5">
          <p className="text-[12px] font-medium text-slate-500">{t.editor.fields.footerAlign}</p>
          <Segmented
            size="sm"
            full
            label={t.editor.fields.footerAlign}
            value={align}
            onChange={(v) => setChrome("align", v)}
            options={FOOTER_ALIGNS.map((value) => {
              const label = value === "start" ? t.editor.fields.footerAlignStart : value === "center" ? t.editor.fields.footerAlignCenter : t.editor.fields.footerAlignSpread;
              const Icon = value === "start" ? AlignLeft : value === "center" ? AlignCenter : AlignJustify;
              return { value, ariaLabel: label, label: (<><Icon aria-hidden="true" />{label}</>) };
            })}
          />
        </div>
        <div className="space-y-1 border-t border-line pt-2">
          <FooterPart id="foot-title" icon={Type} label={t.editor.fields.footerTitle} hint={def.site.logo ? t.editor.fields.footerTitleHint : t.editor.fields.showSiteTitleNoLogo} checked={c.showTitle !== false} onCheckedChange={(v) => setChrome("showTitle", v)} />
          <FooterPart id="foot-social" icon={Share2} label={t.editor.fields.footerSocial} hint={hasSocial ? t.editor.fields.footerSocialHint : empty} checked={c.showSocial !== false} onCheckedChange={(v) => setChrome("showSocial", v)} />
          <FooterPart id="foot-contact" icon={Phone} label={t.editor.fields.footerContact} hint={hasContact ? t.editor.fields.footerContactHint : empty} checked={c.showContact !== false} onCheckedChange={(v) => setChrome("showContact", v)} />
          <FooterPart id="foot-address" icon={MapPin} label={t.editor.fields.footerAddress} hint={hasAddress ? t.editor.fields.footerAddressHint : empty} checked={c.showAddress !== false} onCheckedChange={(v) => setChrome("showAddress", v)} />
          <FooterPart id="foot-hours" icon={Clock} label={t.editor.fields.footerHours} hint={hasHours ? t.editor.fields.footerHoursHint : empty} checked={c.showHours !== false} onCheckedChange={(v) => setChrome("showHours", v)} />
          <FooterPart id="foot-nav" icon={Menu} label={t.editor.fields.footerNav} hint={hasNav ? t.editor.fields.footerNavHint : t.editor.fields.footerNavEmpty} checked={c.showNav !== false} onCheckedChange={(v) => setChrome("showNav", v)} />
        </div>
        <p className="text-[12.5px] leading-snug text-slate-500">{t.editor.fields.footerKept}</p>
      </div>
    </div>
  );
}

function FooterPart({ id, icon: Icon, label, hint, checked, onCheckedChange }: { id: string; icon: LucideIcon; label: string; hint: string; checked: boolean; onCheckedChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-slate-50 text-slate-600 ring-1 ring-slate-900/5">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <SwitchRow id={id} label={label} hint={hint} checked={checked} onCheckedChange={onCheckedChange} />
      </div>
    </div>
  );
}

function Heading({
  eyebrow,
  title,
  description,
  titleOptional,
  onEyebrow,
  onTitle,
  onDescription,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  titleOptional?: boolean;
  onEyebrow: (v: string | undefined) => void;
  onTitle: (v: string | undefined) => void;
  onDescription?: (v: string | undefined) => void;
}) {
  return (
    <>
      <TextField label={t.editor.fields.eyebrow} value={eyebrow ?? ""} optional onChange={(v) => onEyebrow(v || undefined)} />
      <TextField label={t.editor.fields.title} value={title} optional={titleOptional} error={!titleOptional && !title.trim() ? t.editor.requiredField : undefined} onChange={(v) => onTitle(titleOptional ? v || undefined : v)} />
      {onDescription ? <TextField label={t.editor.fields.description} value={description ?? ""} optional multiline onChange={(v) => onDescription(v || undefined)} /> : null}
    </>
  );
}

function TextField({
  label,
  value,
  onChange,
  multiline,
  optional,
  max,
  error,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  optional?: boolean;
  max?: number;
  error?: string;
  placeholder?: string;
}) {
  const { roomy } = useContext(ListFocusContext);
  return (
    <Field label={label} optional={optional} error={error} count={max ? { value: value.length, max } : undefined}>
      {multiline ? (
        <Textarea
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          rows={roomy ? 8 : 4}
          // Large workspace: the box grows with the text (where field-sizing is supported).
          className={roomy ? "max-h-[60dvh] [field-sizing:content]" : undefined}
          style={roomy ? { minHeight: "10rem" } : undefined}
        />
      ) : (
        <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </Field>
  );
}

/**
 * Link input. "shopee.co.id/x" becomes "https://shopee.co.id/x". Only valid, safe links are written to the
 * definition; while the text is invalid the last good link stays live and an error is shown.
 */
function UrlField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  const normalize = (v: string) => {
    const s = v.trim();
    return s && !/^(?:[a-z][a-z0-9+.-]*:|#|\/)/i.test(s) ? `https://${s}` : s;
  };
  const valid = isSafeHref(normalize(draft));
  return (
    <Field label={t.editor.fields.url} error={valid ? undefined : t.editor.fields.urlInvalid}>
      <Input
        inputMode="url"
        value={draft}
        placeholder="https://shopee.co.id/…"
        onChange={(e) => {
          setDraft(e.target.value);
          const next = normalize(e.target.value);
          if (isSafeHref(next)) onChange(next);
        }}
      />
    </Field>
  );
}

/**
 * Optional link (PDF, video). Empty clears it; while the text is invalid the last good link stays live
 * and an error is shown.
 */
function LinkField({
  label,
  value,
  placeholder,
  valid = isSafeHref,
  invalidText = t.editor.fields.urlInvalid,
  onChange,
}: {
  label: string;
  value?: string;
  placeholder?: string;
  valid?: (v: string) => boolean;
  invalidText?: string;
  onChange: (v: string | undefined) => void;
}) {
  const [draft, setDraft] = useState(value ?? "");
  const normalize = (v: string) => {
    const s = v.trim();
    return s && !/^[a-z][a-z0-9+.-]*:/i.test(s) ? `https://${s}` : s;
  };
  const next = normalize(draft);
  const ok = !next || (isSafeHref(next) && valid(next));
  return (
    <Field label={label} optional error={ok ? undefined : invalidText}>
      <Input
        inputMode="url"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => {
          setDraft(e.target.value);
          const v = normalize(e.target.value);
          if (!v) onChange(undefined);
          else if (isSafeHref(v) && valid(v)) onChange(v);
        }}
      />
    </Field>
  );
}

function PriceField({ value, onChange }: { value?: number; onChange: (v: number | undefined) => void }) {
  return (
    <Field label={t.editor.fields.price} optional>
      <Input inputMode="numeric" leading={<span className="text-xs font-semibold">Rp</span>} value={formatRupiahDigits(value)} onChange={(e) => onChange(parseRupiahInput(e.target.value))} />
    </Field>
  );
}

function CtaField({
  label,
  value,
  required,
  initial = { label: "Chat WhatsApp", kind: "whatsapp" },
  hint,
  onChange,
}: {
  label: string;
  value?: Cta;
  required?: boolean;
  initial?: Cta;
  hint?: string;
  onChange: (v: Cta | undefined) => void;
}) {
  const { roomy } = useContext(ListFocusContext);
  if (!value) {
    return (
      <Button variant="secondary" size="sm" onClick={() => onChange(initial)}>
        <Plus aria-hidden="true" />
        {label}
      </Button>
    );
  }
  return (
    <div className="space-y-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-900/8">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">{label}</p>
        {required ? null : (
          <button type="button" className="text-xs font-semibold text-slate-500 hover:text-danger" onClick={() => onChange(undefined)}>
            {t.editor.inspector.ctaRemove}
          </button>
        )}
      </div>
      {/* Wide panel: the two short fields side by side. */}
      <div className={cn(roomy ? "grid gap-3 sm:grid-cols-2" : "space-y-2")}>
        <TextField label={t.editor.inspector.ctaLabel} value={value.label} onChange={(v) => onChange({ ...value, label: v || value.label })} />
        <Field label={t.editor.inspector.ctaKind}>
          <NativeSelect selectSize={roomy ? "md" : "sm"} value={value.kind} onChange={(e) => onChange({ ...value, kind: e.target.value as Cta["kind"] })}>
            <option value="whatsapp">{t.editor.inspector.ctaKinds.whatsapp}</option>
            <option value="link">{t.editor.inspector.ctaKinds.link}</option>
          </NativeSelect>
        </Field>
      </div>
      {value.kind === "link" ? (
        <>
          <TextField label={t.editor.inspector.ctaHref} value={value.href ?? ""} placeholder="/donasi" onChange={(v) => onChange({ ...value, href: v || undefined })} />
          {hint ? <p className="text-xs leading-relaxed text-slate-500">{hint}</p> : null}
        </>
      ) : (
        <TextField label={t.editor.inspector.ctaMessage} value={value.message ?? ""} optional multiline onChange={(v) => onChange({ ...value, message: v || undefined })} />
      )}
    </div>
  );
}

const CUSTOM = "__custom";

/**
 * Where the navbar button goes: automatic (WhatsApp, else contact, else phone), a WhatsApp message, or a
 * link — one of the site's pages, a section of this page, or any other URL.
 */
function NavCtaTarget({ def, section, apply }: { def: WebsiteDefinition; section: Extract<SectionNode, { type: "navbar" }>; apply: Apply }) {
  const c = section.content;
  const { roomy } = useContext(ListFocusContext);
  const patch = (next: Partial<typeof c>) => {
    const merged = Object.fromEntries(Object.entries({ ...c, ...next }).filter(([, v]) => v !== undefined));
    apply(updateContent(def, section.id, "content", merged), `${section.id}:ctaTarget`);
  };
  const page = def.pages.find((p) => p.sections.some((s) => s.id === section.id));
  const targets = [
    ...def.pages.map((p) => ({ href: p.slug, label: `${t.editor.fields.navCtaPage}: ${p.title}` })),
    ...(page?.sections ?? [])
      .filter((s) => s.type !== "navbar" && !s.hidden)
      .map((s) => ({ href: `#${s.anchor}`, label: `${t.editor.fields.navCtaSection}: ${sectionHeading(s) ?? SECTION_META[s.type].label}` })),
  ];
  const known = c.ctaHref !== undefined && targets.some((x) => x.href === c.ctaHref);
  const [custom, setCustom] = useState(c.ctaHref !== undefined && !known);
  const [draft, setDraft] = useState(known ? "" : (c.ctaHref ?? ""));
  const draftOk = !draft.trim() || isSafeHref(draft.trim());
  return (
    <>
      <Field label={t.editor.inspector.ctaKind}>
        <NativeSelect
          selectSize={roomy ? "md" : "sm"}
          value={c.ctaKind ?? ""}
          onChange={(e) => {
            const kind = (e.target.value || undefined) as typeof c.ctaKind;
            patch({ ctaKind: kind, ctaHref: undefined, ctaMessage: undefined });
            setCustom(false);
            setDraft("");
          }}
        >
          <option value="">{t.editor.fields.navCtaAuto}</option>
          <option value="whatsapp">{t.editor.inspector.ctaKinds.whatsapp}</option>
          <option value="link">{t.editor.inspector.ctaKinds.link}</option>
        </NativeSelect>
      </Field>
      {c.ctaKind === "whatsapp" ? (
        <TextField label={t.editor.inspector.ctaMessage} value={c.ctaMessage ?? ""} optional multiline onChange={(v) => patch({ ctaMessage: v || undefined })} />
      ) : null}
      {c.ctaKind === "link" ? (
        <>
          <Field label={t.editor.fields.navCtaTarget}>
            <NativeSelect
              selectSize={roomy ? "md" : "sm"}
              value={custom ? CUSTOM : (c.ctaHref ?? "")}
              onChange={(e) => {
                const v = e.target.value;
                setCustom(v === CUSTOM);
                if (v === CUSTOM) patch({ ctaHref: draft.trim() && isSafeHref(draft.trim()) ? draft.trim() : undefined });
                else patch({ ctaHref: v || undefined });
              }}
            >
              <option value="">{t.editor.fields.navCtaPick}</option>
              {targets.map((x) => (
                <option key={x.href} value={x.href}>
                  {x.label}
                </option>
              ))}
              <option value={CUSTOM}>{t.editor.fields.navCtaCustom}</option>
            </NativeSelect>
          </Field>
          {custom ? (
            <Field label={t.editor.inspector.ctaHref} error={draftOk ? undefined : t.editor.fields.urlInvalid}>
              <Input
                inputMode="url"
                value={draft}
                placeholder={t.editor.inspector.ctaHrefPlaceholder}
                onChange={(e) => {
                  setDraft(e.target.value);
                  const v = e.target.value.trim();
                  if (!v) patch({ ctaHref: undefined });
                  else if (isSafeHref(v)) patch({ ctaHref: v });
                }}
              />
            </Field>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/** One question on the donation form: label, input type, required, placeholder, and the choices of a "select". */
function DonationFieldEditor({ field, update }: { field: DonationField; update: (patch: Partial<DonationField>) => void }) {
  const id = useId();
  return (
    <>
      <TextField label={t.editor.fields.questionLabel} value={field.label} onChange={(v) => update({ label: v.slice(0, 40) || field.label })} />
      <Field label={t.editor.fields.questionType}>
        <NativeSelect
          selectSize="sm"
          value={field.type}
          onChange={(e) => {
            const type = e.target.value as DonationField["type"];
            update({ type, options: type === "select" ? (field.options ?? ["Pilihan 1", "Pilihan 2"]) : undefined });
          }}
        >
          {DONATION_FIELD_TYPES.map((type) => (
            <option key={type} value={type}>
              {t.editor.fields.questionTypes[type]}
            </option>
          ))}
        </NativeSelect>
      </Field>
      {field.type === "select" ? (
        <StringList label={t.editor.fields.questionOptions} itemLabel="Pilihan" items={field.options ?? []} onChange={(options) => update({ options: options.slice(0, 12) })} />
      ) : null}
      <TextField label={t.editor.fields.questionPlaceholder} value={field.placeholder ?? ""} optional onChange={(v) => update({ placeholder: v.slice(0, 80) || undefined })} />
      <SwitchRow id={`${id}-required`} label={t.editor.fields.questionRequired} checked={!!field.required} onCheckedChange={(v) => update({ required: v || undefined })} />
    </>
  );
}

/** Comma-separated rupiah amounts ("50.000, 100.000"). Written on blur, so a half-typed list is never trimmed mid-edit. */
function AmountListField({ label, value, onChange }: { label: string; value: number[]; onChange: (v: number[]) => void }) {
  const show = (v: number[]) => v.map((n) => formatRupiahDigits(n)).join(", ");
  const [draft, setDraft] = useState(() => show(value));
  return (
    <Field label={label} optional>
      <Input
        inputMode="numeric"
        value={draft}
        placeholder="50.000, 100.000, 250.000"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const next = draft
            .split(",")
            .map((p) => parseRupiahInput(p))
            .filter((n): n is number => n !== undefined && n >= 1000 && n <= 1_000_000_000)
            .slice(0, 6);
          setDraft(show(next));
          onChange(next);
        }}
      />
    </Field>
  );
}

function StringList({ label, itemLabel, items, onChange }: { label: string; itemLabel: string; items: string[]; onChange: (items: string[]) => void }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-slate-800">{label}</p>
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input value={item} aria-label={t.editor.inspector.itemN(itemLabel, index + 1)} onChange={(e) => onChange(items.map((v, i) => (i === index ? e.target.value || v : v)))} />
          <button type="button" className="grid size-9 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-danger-soft hover:text-danger" aria-label={t.editor.inspector.removeItem} onClick={() => onChange(items.filter((_, i) => i !== index))}>
            <Trash2 className="size-4" aria-hidden="true" />
          </button>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={() => onChange([...items, itemLabel])}>
        <Plus aria-hidden="true" />
        {t.editor.inspector.addItem}
      </Button>
    </div>
  );
}

/**
 * A list of items (people, services, accounts…). One item is open at a time so long lists stay scannable;
 * items can be reordered, and a removed item can be brought back from the toast.
 */
function ItemStack<T extends object>({
  label,
  path,
  items,
  blank,
  titleOf,
  onChange,
  render,
}: {
  label: string;
  /** Content key of this list, relative to the enclosing item ("items", "groups", "topics"). */
  path: string;
  items: T[];
  blank: () => NoInfer<T>;
  titleOf: (item: T) => string;
  onChange: (items: T[]) => void;
  render: (item: T, update: (patch: Partial<T>) => void) => React.ReactNode;
}) {
  const { focus } = useContext(ListFocusContext);
  const listPath = [...useContext(ItemPathContext), path];
  const at = focus && "path" in focus && startsWith(focus.path, listPath) ? Number(focus.path[listPath.length]) : null;
  const [open, setOpen] = useState<number | null>(0);
  // Sidebar: a click on an item in the preview opens it here (each click bumps the nonce).
  const [seenNonce, setSeenNonce] = useState(0);
  const openNonce = focus?.mode === "open" ? focus.nonce : 0;
  if (openNonce !== seenNonce) {
    setSeenNonce(openNonce);
    if (at !== null && at < items.length) setOpen(at);
  }
  const target = focus?.mode === "open" && at !== null && focus.path.length === listPath.length + 1 ? at : null;
  const targetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (openNonce && target !== null) targetRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [openNonce, target]);
  // The toast's undo runs later: put the item back into the list as it is then, not as it was.
  const latest = useRef(items);
  useEffect(() => {
    latest.current = items;
  }, [items]);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row!);
    onChange(next);
    setOpen((cur) => (cur === from ? to : cur === to ? from : cur));
  };
  const remove = (index: number) => {
    const removed = items[index]!;
    onChange(items.filter((_, i) => i !== index));
    setOpen(null);
    toast(t.editor.inspector.itemRemoved(titleOf(removed) || t.editor.inspector.itemN(label, index + 1)), {
      action: {
        label: t.common.undo,
        onClick: () => {
          const now = latest.current;
          onChange([...now.slice(0, index), removed, ...now.slice(index)]);
          setOpen(index);
        },
      },
    });
  };
  const update = (index: number) => (patch: Partial<T>) => onChange(items.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const withPath = (index: number, node: React.ReactNode) => <ItemPathContext.Provider value={[...listPath, String(index)]}>{node}</ItemPathContext.Provider>;
  // Large workspace: lists are edited from its tree, so a section's settings show none of them and
  // an item shows only itself (the fields of items it is nested in stay hidden).
  if (focus?.mode === "settings") return null;
  if (focus?.mode === "item") {
    if (at === null || !items[at]) return null;
    const deeper = focus.path.length > listPath.length + 1;
    return (
      <div data-item-stack="" className={deeper ? "[&>:not([data-item-stack])]:hidden" : "space-y-3"}>
        {withPath(at, render(items[at], update(at)))}
      </div>
    );
  }
  return (
    <div className="space-y-2" data-item-stack="">
      <p className="text-sm font-semibold text-slate-800">{label}</p>
      {items.map((item, index) => {
        const expanded = open === index;
        const title = titleOf(item) || t.editor.inspector.itemN(label, index + 1);
        return (
          <div key={index} ref={index === target ? targetRef : undefined} className="scroll-mt-4 rounded-xl bg-slate-50 ring-1 ring-slate-900/8">
            <div className="flex items-center gap-1 py-1 pl-1 pr-2">
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setOpen(expanded ? null : index)}
                className="flex min-h-9 min-w-0 flex-1 items-center gap-2 rounded-lg px-2 text-left text-sm font-semibold text-slate-800 hover:bg-white"
              >
                <ChevronDown className={cn("size-4 shrink-0 text-slate-400 transition-transform", !expanded && "-rotate-90")} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{title}</span>
              </button>
              <button type="button" aria-label={`${t.editor.inspector.moveItemUp}: ${title}`} disabled={index === 0} onClick={() => move(index, index - 1)} className="grid size-8 place-items-center rounded-lg text-slate-500 hover:bg-white hover:text-slate-900 disabled:opacity-30">
                <ChevronUp className="size-4" aria-hidden="true" />
              </button>
              <button type="button" aria-label={`${t.editor.inspector.moveItemDown}: ${title}`} disabled={index === items.length - 1} onClick={() => move(index, index + 1)} className="grid size-8 place-items-center rounded-lg text-slate-500 hover:bg-white hover:text-slate-900 disabled:opacity-30">
                <ChevronDown className="size-4" aria-hidden="true" />
              </button>
              <button type="button" aria-label={`${t.editor.inspector.removeItem}: ${title}`} onClick={() => remove(index)} className="grid size-8 place-items-center rounded-lg text-slate-500 hover:bg-white hover:text-danger">
                <Trash2 className="size-4" aria-hidden="true" />
              </button>
            </div>
            {expanded ? <div className="space-y-3 px-3 pb-3 pt-1">{withPath(index, render(item, update(index)))}</div> : null}
          </div>
        );
      })}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          onChange([...items, blank()]);
          setOpen(items.length);
        }}
      >
        <Plus aria-hidden="true" />
        {t.editor.inspector.addItem}
      </Button>
    </div>
  );
}

export function ImageField({
  siteId,
  label,
  value,
  onChange,
  fit = "cover",
  hint,
  uploaded,
}: {
  siteId: string;
  label: string;
  value?: Img;
  onChange: (v: Img | undefined) => void;
  fit?: "cover" | "contain";
  hint?: string;
  uploaded?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [over, setOver] = useState(false);
  const upload = useMutation({
    mutationFn: (file: File) => api.assets.upload(siteId, file),
    meta: { silent: true },
    onSuccess: (asset) => {
      onChange({ src: asset.url, alt: value?.alt ?? "", assetId: asset.id, width: asset.width, height: asset.height });
      toast.success(uploaded ?? t.editor.image.uploaded);
    },
    onError: () => toast.error(t.editor.image.badType),
  });
  const pick = (file: File | undefined) => {
    if (!file) return;
    const ok = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
    if (!ok) {
      toast.error(t.editor.image.badType);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t.editor.image.tooLarge);
      return;
    }
    upload.mutate(file);
  };
  const draggingFile = (e: DragEvent) => [...e.dataTransfer.types].includes("Files");
  return (
    <Field label={label} optional hint={value ? undefined : (hint ?? t.editor.image.uploadHint)}>
      <div
        className={cn("space-y-2 rounded-xl", over && "ring-2 ring-primary")}
        onDragEnter={(e) => {
          if (!draggingFile(e)) return;
          e.preventDefault();
          dragDepth.current += 1;
          setOver(true);
        }}
        onDragOver={(e) => {
          if (!draggingFile(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setOver(false);
        }}
        onDrop={(e) => {
          if (!draggingFile(e)) return;
          e.preventDefault();
          dragDepth.current = 0;
          setOver(false);
          const file = [...e.dataTransfer.files].find((item) => item.type.startsWith("image/"));
          if (!file && e.dataTransfer.files.length) toast.error(t.editor.image.badType);
          pick(file);
        }}
      >
        {over ? <p className="text-sm font-semibold text-primary">{t.editor.image.dropHere}</p> : null}
        {value ? (
          <div className={fit === "contain" ? "grid h-24 place-items-center rounded-xl bg-slate-50 ring-1 ring-slate-900/10" : "overflow-hidden rounded-xl ring-1 ring-slate-900/10"}>
            <img src={value.src} alt="" className={fit === "contain" ? "max-h-16 max-w-[70%] object-contain" : "h-28 w-full object-cover"} />
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" loading={upload.isPending} onClick={() => input.current?.click()}>
            <ImagePlus aria-hidden="true" />
            {value ? t.editor.image.change : t.editor.image.upload}
          </Button>
          {value ? (
            <Button variant="ghost" size="sm" onClick={() => onChange(undefined)}>
              {t.editor.image.remove}
            </Button>
          ) : null}
        </div>
        {value ? <Input aria-label={t.editor.image.alt} placeholder={t.editor.image.alt} value={value.alt} onChange={(e) => onChange({ ...value, alt: e.target.value })} /> : null}
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    </Field>
  );
}

const PER_PAGE_OPTIONS = ["5", "8", "10", "15", "20", "0"] as const;

/** Topics per page in the lesson list; the hint says what visitors get with the current material. */
function LessonsPerPage({ perPage, total, onChange }: { perPage: number | undefined; total: number; onChange: (v: number | undefined) => void }) {
  const per = perPage ?? LESSONS_PER_PAGE;
  const value = (PER_PAGE_OPTIONS as readonly string[]).includes(String(per)) ? String(per) : String(LESSONS_PER_PAGE);
  const pages = per > 0 ? Math.ceil(total / per) : 1;
  return (
    <Field label={t.editor.fields.lessonPerPage} hint={per > 0 && pages > 1 ? t.editor.fields.lessonPerPageHint(total, pages) : t.editor.fields.lessonPerPageAll(total)}>
      <Segmented
        size="sm"
        full
        label={t.editor.fields.lessonPerPage}
        value={value}
        onChange={(v) => onChange(Number(v) === LESSONS_PER_PAGE ? undefined : Number(v))}
        options={PER_PAGE_OPTIONS.map((o) => ({ value: o, label: o === "0" ? t.editor.fields.lessonPerPageNone : o }))}
      />
    </Field>
  );
}
